import React, { useEffect } from "react";
import Script from "next/script";
import { ThemeProvider } from "@material-ui/core/styles";
import Head from "next/head";
import CssBaseline from "@material-ui/core/CssBaseline";
import moment from "moment";
import "moment/locale/fr";
import * as Sentry from "@sentry/node";
import { RewriteFrames } from "@sentry/integrations";
import getConfig from "next/config";
import { Hydrate } from "react-query/hydration";
import { QueryClient, QueryClientProvider } from "react-query";

import GTMPageViewTag from "../components/gtmTag/pageView";
import theme from "../theme";
import CartProvider from "../components/cart/CartProvider";
import UserProvider from "../components/user/UserProvider";
import HomeRepairAuthProvider from "../components/HomeRepairAuthProvider";
import "fonts/index.css";
import FilterProvider from "components/filters/FilterProvider";
import SeoApp from "components/seo/app";
import queryFn from "helpers/queryFn";
import { gtmPush } from "lib/gtm";
import "./styles.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn,
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});

// TODO replace by website locale
moment.locale("fr");

if (process.env.NEXT_PUBLIC_SENTRY_DSN) {
  const environmentMap = {
    preview: "PREPROD",
    production: "MASTER",
  };
  const config = getConfig();
  const distDir = `${config.serverRuntimeConfig.rootDir}/.next`;
  Sentry.init({
    enabled: !!environmentMap[process.env.NEXT_PUBLIC_VERCEL_ENV],
    integrations: [
      new RewriteFrames({
        iteratee: (frame) => {
          frame.filename = frame.filename.replace(distDir, "app:///_next");
          return frame;
        },
      }),
    ],
    dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
    environment: environmentMap[process.env.NEXT_PUBLIC_VERCEL_ENV],
    autoSessionTracking: false,
  });
}

export function reportWebVitals({ id, name, label, value }) {
  gtmPush({
    event: "GAevent",
    non_interaction: true,
    category: label === "web-vital" ? "Web Vitals" : "Next.js custom metric",
    action: name,
    value: Math.round(name === "CLS" ? value * 1000 : value),
    label: id,
  });
}

// err prop is a workaround for https://github.com/vercel/next.js/issues/8592
function MyApp({ Component, pageProps, err }) {
  // Remove material-ui server side generated style in order to inject stylesheet on client side, generated in _document.js
  // See https://material-ui.com/guides/server-rendering/#react-class-name-hydration-mismatch
  useEffect(() => {
    const jssStyles = document.querySelector("#jss-server-side");
    if (jssStyles && jssStyles.parentNode) {
      jssStyles.parentNode.removeChild(jssStyles);
    }
  }, []);

  useEffect(() => {
    window._axcb = window._axcb ?? [];

    window._axcb.push((sdk) => {
      sdk.on("consent:saved", () => {
        window.location.reload();
      });
    });
  }, []);

  const getLayout = Component.getLayout || ((page) => page);
  return (
    <>
      <Script id="axeptio" strategy="afterInteractive">
        {`
        window.axeptioSettings = {
          clientId: "${process.env.NEXT_PUBLIC_AXEPTIO_CLIENT_ID}",
          cookiesVersion: "${process.env.NEXT_PUBLIC_AXEPTIO_COOKIES_VERSION}",
        };
       
        (function(d, s) {
          var t = d.getElementsByTagName(s)[0], e = d.createElement(s);
          e.async = true; e.src = "//static.axept.io/sdk-slim.js";
          t.parentNode.insertBefore(e, t);
        })(document, "script");`}
      </Script>
      <Script id="customerio" strategy="beforeInteractive">
        {`
          var _cio = _cio || [];
          (function() {
              var a,b,c;a=function(f){return function(){_cio.push([f].
              concat(Array.prototype.slice.call(arguments,0)))}};b=["load","identify",
              "sidentify","track","page"];for(c=0;c<b.length;c++){_cio[b[c]]=a(b[c])};
              var t = document.createElement('script'),
                  s = document.getElementsByTagName('script')[0];
              t.async = true;
              t.id    = 'cio-tracker';
              t.setAttribute('data-site-id', '${process.env.NEXT_PUBLIC_CUSTOMER_IO_SITE_ID}');
              t.setAttribute('data-use-array-params', 'true');
              t.src = 'https://assets.customer.io/assets/track.js';
              s.parentNode.insertBefore(t, s);
          })();
        `}
      </Script>
      <ThemeProvider theme={theme}>
        <Head>
          <link rel="icon" href="/reconditionne/favicon.ico" />
        </Head>
        <SeoApp />
        <CssBaseline />
        <HomeRepairAuthProvider>
          <UserProvider>
            <QueryClientProvider client={queryClient}>
              <Hydrate state={pageProps.dehydratedState}>
                <CartProvider>
                  <FilterProvider>{getLayout(<Component {...pageProps} err={err} />)}</FilterProvider>
                </CartProvider>
              </Hydrate>
            </QueryClientProvider>
            <GTMPageViewTag />
          </UserProvider>
        </HomeRepairAuthProvider>
      </ThemeProvider>
    </>
  );
}

export default MyApp;
